import { InputHTMLAttributes } from 'react';
import styled from '@emotion/styled';
import { Text } from 'components/core/text';
import { Star } from 'components/core/text/Star';
import { borderRadius, color } from 'components/core/theme';
import { mediaDesktop } from 'components/core/theme/breakpoints';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: boolean;
  helperText?: string;
  requiredStar?: boolean;
  wrapperClassNames?: string;
  outerClassnames?: string;
}

const Wrapper = styled.div`
  position: relative;
`;

const _Input = styled.input<{ error?: boolean }>`
  width: 100%;
  background-color: rgba(0, 0, 0, 0);
  outline: none;
  border: 1px solid
    ${props => (props.error ? color('error') : color('lightGreyDark'))(props)};
  border-radius: ${borderRadius.s};
  color: ${color('greyDark')};
  transition: 0.1s ease-out;

  font-size: 1.125rem;
  padding: 13px 12px;
  ${mediaDesktop} {
    padding: 14px 16px;
    font-size: 1.25rem;
  }

  ::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    opacity: 0;
  }

  :-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    opacity: 0;
  }

  ::-ms-input-placeholder {
    /* Microsoft Edge */
    opacity: 0;
  }

  :hover {
    border-color: #e7f9ed;
  }

  :focus {
    border-color: ${color('greenDark')};
  }

  :focus + label {
    color: ${color('greyDark')};
    top: 18px;
    transform: translateY(-50%) scale(0.75);
  }

  :focus,
  :not(:placeholder-shown) {
    padding: 20px 12px 6px 12px;
  }
  ${mediaDesktop} {
    :focus,
    :not(:placeholder-shown) {
      padding: 21px 12px 7px 12px;
    }
  }

  :not(:placeholder-shown) + label {
    top: 18px;
    transform: translateY(-50%) scale(0.75);
  }
`;

const Label = styled.label<{ error?: boolean }>`
  position: absolute;
  font-size: 1rem;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  background-color: white;
  color: ${props => (props.error ? color('error') : color('greyLight'))(props)};
  padding: 0 0.3rem;
  margin: 0 0.5rem;
  transition: 0.1s ease-out;
  transform-origin: left top;
  pointer-events: none;
`;

export const InputHelperText = styled(Text)<{ error?: boolean }>`
  margin-left: 14px;
  color: ${props =>
    props.error ? color('error')(props) : color('greyDark')(props)};

  font-size: 11px;
  line-height: 14px;

  ${mediaDesktop} {
    font-size: 13px;
    line-height: 19px;
  }
`;

export function Input(props: InputProps) {
  const {
    label,
    helperText,
    error,
    type,
    requiredStar,
    wrapperClassNames,
    outerClassnames,
    ...rest
  } = props;

  return (
    <Wrapper className={`w-full ${outerClassnames}`}>
      <Wrapper className={wrapperClassNames}>
        <_Input error={error} type={type} placeholder={label} {...rest} />
        <Label error={error}>
          {label}
          {requiredStar && <Star />}
        </Label>
        {helperText && (
          <InputHelperText block error={error} variant="text6">
            {helperText}
          </InputHelperText>
        )}
      </Wrapper>
    </Wrapper>
  );
}
